import { Button, CircularProgress, Collapse, Stack, TextField } from "@mui/material";
import { useState } from "react";
import { MediumText, SmallText } from "../../common/styles/Text.styles";
import {
  borderStyle,
  perelynBlack,
  perelynPrimary,
  smallGap,
  smallerGap,
  defaultTransition,
} from "../../styles/consts";
import RejectionReasonButton from "./RejectionReasonButton";

const PREDEFINED_REASONS = [
  "On site",
  "Lack of qualification",
  "Hourly rate",
  "Contract type",
  "Timeline",
];

interface RejectionPopupContentProps {
  onSubmit: (selectedReasons: string[], freeText: string) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

export default function RejectionPopupContent({
  onSubmit,
  onCancel,
  isLoading,
}: RejectionPopupContentProps) {
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [freeText, setFreeText] = useState("");
  const [isTextFieldExpanded, setIsTextFieldExpanded] = useState(false);

  const toggleReason = (reason: string) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason]
    );
  };

  const handleSubmit = async () => {
    await onSubmit(selectedReasons, freeText);
  };

  const isSubmitDisabled =
    (selectedReasons.length === 0 && freeText.trim() === "") || isLoading;

  return (
    <Stack
      sx={{
        border: borderStyle,
        padding: smallGap,
        gap: smallGap,
      }}
    >
      <Stack sx={{ width: "100%", gap: smallGap }}>
        <MediumText>Why are you rejecting this offer?</MediumText>
        <SmallText sx={{ color: "#666" }}>
          Select one or more reasons below or add your own comment. This
          information will be sent to your supervisor.
        </SmallText>

        {/* Predefined reasons as selectable buttons */}
        <Stack sx={{ flexDirection: "row", gap: smallGap, flexWrap: "wrap" }}>
          {PREDEFINED_REASONS.map((reason) => (
            <RejectionReasonButton
              key={reason}
              reason={reason}
              isSelected={selectedReasons.includes(reason)}
              onClick={() => toggleReason(reason)}
            />
          ))}
        </Stack>

        {/* Collapsible free text section */}
        <Stack sx={{ gap: smallGap }}>
          <Stack
            onClick={() => setIsTextFieldExpanded(!isTextFieldExpanded)}
            sx={{
              flexDirection: "row",
              alignItems: "center",
              gap: "0.5rem",
              cursor: "pointer",
              "&:hover": {
                "& *": {
                  color: perelynPrimary,
                },
              },
            }}
          >
            <SmallText
              sx={{
                color: "#666",
                transition: defaultTransition,
              }}
            >
              {isTextFieldExpanded
                ? "Hide additional comments"
                : "Add additional comments"}
            </SmallText>
            <img
              src="/svg/icons/BigChevronDown.svg"
              alt="expand"
              style={{
                width: "1.5rem",
                height: "1.5rem",
                transform: isTextFieldExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            />
          </Stack>

          <Collapse in={isTextFieldExpanded}>
            <TextField
              id="rejection-free-text"
              placeholder="Add any additional details here..."
              multiline
              minRows={5}
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              sx={{ width: "100%" }}
            />
          </Collapse>
        </Stack>
      </Stack>

      <Stack
        sx={{
          flexDirection: "row",
          justifyContent: "end",
          gap: smallGap,
        }}
      >
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          sx={{
            "&&": { width: "8rem" },
            ":hover": {
              "& .MuiCircularProgress-root": {
                color: perelynPrimary,
              },
            },
          }}
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          {isLoading ? (
            <CircularProgress sx={{ color: perelynBlack }} size={smallerGap} />
          ) : (
            "Reject Offer"
          )}
        </Button>
      </Stack>
    </Stack>
  );
}
